import { Column, RowData } from '@tanstack/react-table';

// How a column is rendered when the Table collapses into stacked cards on
// phone-sized viewports. Columns without an explicit role fall back to:
// first accessor column -> 'title', other accessor columns -> 'detail',
// display-only columns (expander, actions without a role) -> 'hidden'.
export type CardRole =
    | 'title'
    | 'subtitle'
    | 'chip'
    | 'badge'
    | 'detail'
    | 'actions'
    | 'selection'
    | 'hidden';

export type TableColumnMeta = {
    disableEllipsis?: boolean,
    cardRole?: CardRole,
    cardLabel?: string,
    cardOrder?: number,
    // When true, this cell is omitted from the stacked-card layout if its
    // value is empty (null/undefined/'' or an empty array), instead of
    // rendering a labeled row with a placeholder.
    hideCardWhenEmpty?: boolean,
};

declare module '@tanstack/react-table' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends RowData, TValue> extends TableColumnMeta {}
}

export const getColumnMeta = <T,>(column: Column<T, unknown>): TableColumnMeta =>
    (column.columnDef.meta as TableColumnMeta) ?? {};

export const resolveCardRole = <T,>(
    column: Column<T, unknown>,
    isFirstAccessorColumn: boolean,
): CardRole => {
    const explicitRole = getColumnMeta(column).cardRole;
    if (explicitRole) {
        return explicitRole;
    }
    if (!column.accessorFn) {
        return 'hidden';
    }

    return isFirstAccessorColumn ? 'title' : 'detail';
};

export const getCardLabel = <T,>(column: Column<T, unknown>): string => {
    const { cardLabel } = getColumnMeta(column);
    if (cardLabel) {
        return cardLabel;
    }
    const { header } = column.columnDef;

    return typeof header === 'string' ? header : '';
};
